/* import __COLOCATED_TEMPLATE__ from './semifinal-startlist-interface.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { TrackedArray } from 'tracked-built-ins';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

export default class HeatStartlistComponent extends Component
{
	@service store;
	@service csv;
	@service sprintTools;
	
	@tracked quarterfinalsEvent;
	
	@tracked startTimeS1;
	@tracked startTimeS2;
	
	@tracked Q1ClassGroup;
	@tracked Q2ClassGroup;
	@tracked Q3ClassGroup;
	@tracked Q4ClassGroup;
	@tracked Q5ClassGroup;
	
	@tracked S1ClassGroup;
	@tracked S2ClassGroup;
	
	@tracked S1Assignments;
	@tracked S2Assignments;
	
	@tracked LL1;
	@tracked LL2;
	@tracked LLOthers;
	
	@action
	async quarterfinalsEventChanged(browserEvent)
	{
		if(browserEvent.target.value == '-')
		{
			this.quarterfinalsEvent = null;
		}
		else
		{
			var event = await this.store.findRecord('Event', browserEvent.target.value);
			this.quarterfinalsEvent = event;
		}
		
		if(this.quarterfinalsEvent?.id)
		{
			await this.sprintTools.fetchResultsForEvent(this.quarterfinalsEvent.id);
		}
		this.Q1ClassGroup = null;
		this.Q2ClassGroup = null;
		this.Q3ClassGroup = null;
		this.Q4ClassGroup = null;
		this.Q5ClassGroup = null;
	}
	
	@action
	async Q1ClassGroupChanged(browserEvent)
	{
		if(browserEvent.target.value == '-')
		{
			this.Q1ClassGroup = null;
		}
		else
		{
			var classGroup = await this.store.findRecord('ClassGroup', browserEvent.target.value);
			this.Q1ClassGroup = classGroup;
		}
		this.calculateHeatAssignments();
	}
	
	@action
	async Q2ClassGroupChanged(browserEvent)
	{
		if(browserEvent.target.value == '-')
		{
			this.Q2ClassGroup = null;
		}
		else
		{
			var classGroup = await this.store.findRecord('ClassGroup', browserEvent.target.value);
			this.Q2ClassGroup = classGroup;
		}
		this.calculateHeatAssignments();
	}
	
	@action
	async Q3ClassGroupChanged(browserEvent)
	{
		if(browserEvent.target.value == '-')
		{
			this.Q3ClassGroup = null;
		}
		else
		{
			var classGroup = await this.store.findRecord('ClassGroup', browserEvent.target.value);
			this.Q3ClassGroup = classGroup;
		}
		this.calculateHeatAssignments();
	}
	
	@action
	async Q4ClassGroupChanged(browserEvent)
	{
		if(browserEvent.target.value == '-')
		{
			this.Q4ClassGroup = null;
		}
		else
		{
			var classGroup = await this.store.findRecord('ClassGroup', browserEvent.target.value);
			this.Q4ClassGroup = classGroup;
		}
		this.calculateHeatAssignments();
	}
	
	@action
	async Q5ClassGroupChanged(browserEvent)
	{
		if(browserEvent.target.value == '-')
		{
			this.Q5ClassGroup = null;
		}
		else
		{
			var classGroup = await this.store.findRecord('ClassGroup', browserEvent.target.value);
			this.Q5ClassGroup = classGroup;
		}
		this.calculateHeatAssignments();
	}
	
	@action
	async S1ClassGroupChanged(browserEvent)
	{
		if(browserEvent.target.value == '-')
		{
			this.S1ClassGroup = null;
		}
		else
		{
			var classGroup = await this.store.findRecord('ClassGroup', browserEvent.target.value);
			this.S1ClassGroup = classGroup;
			this.startTimeS1 = dayjs(classGroup.startTime).format("HH:mm:ss");
		}
	}
	
	@action
	async S2ClassGroupChanged(browserEvent)
	{
		if(browserEvent.target.value == '-')
		{
			this.S2ClassGroup = null;
		}
		else
		{
			var classGroup = await this.store.findRecord('ClassGroup', browserEvent.target.value);
			this.S2ClassGroup = classGroup;
			this.startTimeS2 = dayjs(classGroup.startTime).format("HH:mm:ss");
		}
	}
	
	@action
	exportHeatStartListCSV()
	{
		// Check if assignments exist before exporting
		if (!this.S1Assignments || !this.S2Assignments) {
			return;
		}

		var buildData = (assignments, classGroup, startTime) => {
			var data = [...this.sprintTools.getCSVHeaders()];
			for (const result of assignments) {
				data.push(this.sprintTools.buildCSVRow(result, classGroup, startTime, result.bib));
			}
			return data;
		};

		this.csv.export(buildData(this.S1Assignments, this.S1ClassGroup, this.startTimeS1), {fileName:'S1semifinalsStartlist.csv',withSeparator:false});
		this.csv.export(buildData(this.S2Assignments, this.S2ClassGroup, this.startTimeS2), {fileName:'S2semifinalsStartlist.csv',withSeparator:false});
	}
	
	@action
	async createClasses()
	{
		var destinationEvent = this.args.destinationEvent;
		var destinationClasses = await destinationEvent.get('classes');

		await this.sprintTools.createClassesForHeat(destinationEvent, destinationClasses, this.S1ClassGroup, this.S1Assignments);
		await this.sprintTools.createClassesForHeat(destinationEvent, destinationClasses, this.S2ClassGroup, this.S2Assignments);
	}
	
	@action
	async createEntries()
	{
		var destinationEvent = this.args.destinationEvent;
		var destinationEntries = await destinationEvent.get('entries');
		var destinationClasses = await destinationEvent.get('classes');

		// useNewBib = false because this file uses result.bib
		await this.sprintTools.createEntriesForHeat(destinationEvent, destinationEntries, destinationClasses, this.S1ClassGroup, this.S1Assignments, this.startTimeS1, false);
		await this.sprintTools.createEntriesForHeat(destinationEvent, destinationEntries, destinationClasses, this.S2ClassGroup, this.S2Assignments, this.startTimeS2, false);
	}
	
	async calculateLuckyLosers()
	{
		var Q1Results = [...(await this.Q1ClassGroup.resultsSorted)];
		var Q2Results = [...(await this.Q2ClassGroup.resultsSorted)];
		var Q3Results = [...(await this.Q3ClassGroup.resultsSorted)];
		var Q4Results = [...(await this.Q4ClassGroup.resultsSorted)];
		var Q5Results = [...(await this.Q5ClassGroup.resultsSorted)];

		// Collect all 3rd/4th place candidates with valid times
		var luckyLoserCandidates = [];
		var addCandidate = function(result) {
			if(result && result.status == 'OK' && result.totalTimeInMilliseconds != null) {
				luckyLoserCandidates.push(result);
			}
		};

		// Add 3rd and 4th place from each quarterfinal
		addCandidate(Q1Results[2]);
		addCandidate(Q1Results[3]);
		addCandidate(Q2Results[2]);
		addCandidate(Q2Results[3]);
		addCandidate(Q3Results[2]);
		addCandidate(Q3Results[3]);
		addCandidate(Q4Results[2]);
		addCandidate(Q4Results[3]);
		addCandidate(Q5Results[2]);
		addCandidate(Q5Results[3]);

		// Sort by time and take fastest 2 as lucky losers
		luckyLoserCandidates.sort((a, b) => a.totalTimeInMilliseconds - b.totalTimeInMilliseconds);

		this.LL1 = luckyLoserCandidates[0] || null;
		this.LL2 = luckyLoserCandidates[1] || null;

		// Remaining candidates are unlucky losers
		var LLOthers = new TrackedArray();
		for(var i = 2; i < luckyLoserCandidates.length; i++) {
			LLOthers.push(luckyLoserCandidates[i]);
		}
		this.LLOthers = LLOthers;
	}
	
	async calculateHeatAssignments()
	{
		
		var S1Assignments = [];
		var S2Assignments = [];
		
		var Q1ClassGroup = this.Q1ClassGroup;
		var Q2ClassGroup = this.Q2ClassGroup;
		var Q3ClassGroup = this.Q3ClassGroup;
		var Q4ClassGroup = this.Q4ClassGroup;
		var Q5ClassGroup = this.Q5ClassGroup;
		
		if(	Q1ClassGroup == null ||
			Q2ClassGroup == null ||
			Q3ClassGroup == null ||
			Q4ClassGroup == null ||
			Q5ClassGroup == null)
		{
			return false;
		}
		
		await this.calculateLuckyLosers();

		var Q1Results = [...(await this.Q1ClassGroup.resultsSorted)];
		var Q2Results = [...(await this.Q2ClassGroup.resultsSorted)];
		var Q3Results = [...(await this.Q3ClassGroup.resultsSorted)];
		var Q4Results = [...(await this.Q4ClassGroup.resultsSorted)];
		var Q5Results = [...(await this.Q5ClassGroup.resultsSorted)];
		
		let S11s = [];
		S11s.push(Q1Results[0]);
		S11s.push(Q2Results[0]);
		S11s.push(Q3Results[0]);
		
		let S12s = [];
		S12s.push(Q1Results[1]);
		S12s.push(Q2Results[1]);
		
		let S21s = [];
		S21s.push(Q4Results[0]);
		S21s.push(Q5Results[0]);
		
		let S22s = [];
		S22s.push(Q3Results[1]);
		S22s.push(Q4Results[1]);
		S22s.push(Q5Results[1]);
		
		S11s.sort((a,b) => (a.bib - b.bib));
		S12s.sort((a,b) => (a.bib - b.bib));
		S21s.sort((a,b) => (a.bib - b.bib));
		S22s.sort((a,b) => (a.bib - b.bib));
		
		S11s[0].set('heatStartOrder',1);
		S1Assignments.push(S11s[0]);
		S11s[1].set('heatStartOrder',2);
		S1Assignments.push(S11s[1]);
		S11s[2].set('heatStartOrder',3);
		S1Assignments.push(S11s[2]);
		
		S12s[0].set('heatStartOrder',4);
		S1Assignments.push(S12s[0]);
		S12s[1].set('heatStartOrder',5);
		S1Assignments.push(S12s[1]);
		
		S21s[0].set('heatStartOrder',1);
		S2Assignments.push(S21s[0]);
		S21s[1].set('heatStartOrder',2);
		S2Assignments.push(S21s[1]);
		
		S22s[0].set('heatStartOrder',3);
		S2Assignments.push(S22s[0]);
		S22s[1].set('heatStartOrder',4);
		S2Assignments.push(S22s[1]);
		S22s[2].set('heatStartOrder',5);
		S2Assignments.push(S22s[2]);

		//lucky losers - assign by quarterfinal time
		//LL1 is faster (already sorted by totalTimeInMilliseconds), LL2 is slower
		S2Assignments.push(this.LL1);  // faster goes to S2
		S1Assignments.push(this.LL2);  // slower goes to S1
		this.LL1.set('heatStartOrder', 6);
		this.LL2.set('heatStartOrder', 6);

		this.S1Assignments = S1Assignments;
		this.S2Assignments = S2Assignments;
	}
	
}
